import React, { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Autocomplete, TextField } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { createAxiosInstance } from "api/axiosConfig.js";
import {
  GET_DISEASE_CODES,
  PATIENT_FAMILY_HISTORY,
  PATIENT_FAMILY_HISTORY_GET_BYPATIENTID,
} from "api/Service.js";
import MedicalHistoryEmptyState from "assets/images/medical-history-empty-state.svg";
import { Button } from "components/ui/Button";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { Select } from "components/ui/Select/Select";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";

import s from "./FamilyMedicalHistory.module.css";
import {
  EMPTY_FAMILY_HISTORY_FORM,
  toFamilyHistoryForm,
} from "./utils/familyHistoryForm";

const FamilyMedicalHistory = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState({ open: false, id: null });
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [familyRelationship, setFamilyRelationship] = useState(
    EMPTY_FAMILY_HISTORY_FORM.familyRelationship
  );
  const [medicalDetails, setMedicalDetails] = useState(
    EMPTY_FAMILY_HISTORY_FORM.medicalDetails
  );
  const [diseaseCodeData, setDiseaseCodeData] = useState(
    EMPTY_FAMILY_HISTORY_FORM.diseaseCode
  );
  const [deceased, setDeceased] = useState(EMPTY_FAMILY_HISTORY_FORM.deceased);
  const [fullName, setFullName] = useState("");
  const [options, setOptions] = useState([]);

  const patientId = useCurrentPatientId();
  const tenantVal = localStorage.getItem("kinesin-tenant");

  const {
    data: familyData = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["familyMedicalHistory", patientId],
    queryFn: async () => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.get(
        `${tenantVal}${PATIENT_FAMILY_HISTORY}${PATIENT_FAMILY_HISTORY_GET_BYPATIENTID}/${patientId}`
      );
      return response.data;
    },
    enabled: !!patientId && !!tenantVal,
  });

  const deleteMedicalHistory = useCallback(
    async (id) => {
      try {
        const axiosInstance = createAxiosInstance();
        await axiosInstance.delete(
          `${tenantVal}${PATIENT_FAMILY_HISTORY}/delete`,
          { data: { id } }
        );
        queryClient.invalidateQueries({ queryKey: ["familyMedicalHistory"] });
        queryClient.invalidateQueries({ queryKey: [patientId] });
        toast.success("Family medical history record has been deleted");
      } catch (error) {
        toast.error(`Error fetching data: ${error.message}`);
        console.error("There was a problem with the axios operation:", error);
      }
    },
    [tenantVal, patientId, queryClient]
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const loadForm = (values) => {
    setFamilyRelationship(values.familyRelationship);
    setMedicalDetails(values.medicalDetails);
    setDeceased(values.deceased);
    setDiseaseCodeData(values.diseaseCode);
    // The picker shows the code alone: the row stores the code, not its description.
    setFullName(values.diseaseCode || "");
  };

  const handleOpenModal = useCallback(
    (id) => {
      const isEdit = typeof id === "number";

      setIsModalOpen({ open: true, id: isEdit ? id : null });
      loadForm(
        toFamilyHistoryForm(
          isEdit ? familyData.find((item) => item.id === id) : null
        )
      );
    },
    [familyData] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleCloseModal = () => {
    setIsModalOpen({ open: false, id: null });
    clearForm();
  };

  const handleSave2 = async () => {
    const formData = {
      id: isModalOpen.id ?? null,
      familyRelationship,
      medicalDetails,
      diseaseCode: diseaseCodeData,
      deceased,
      patientId,
    };
    try {
      const axiosInstance = createAxiosInstance();
      await axiosInstance.post(
        `${tenantVal}${PATIENT_FAMILY_HISTORY}`,
        formData
      );
      toast.success("Data saved successfully!");
      queryClient.invalidateQueries({
        queryKey: ["familyMedicalHistory", patientId],
      });
      setIsModalOpen({ open: false, id: null });
      clearForm();
    } catch (error) {
      console.error("There was a problem with the axios operation:", error);
    }
  };

  const clearForm = () => loadForm(EMPTY_FAMILY_HISTORY_FORM);

  const onInputChange = (_event, value) => {
    if (value && value.length > 0) {
      getData(value);
    } else {
      setTimeout(() => setOptions([]), 0);
    }
  };

  const getData = async (searchTerm) => {
    if (!searchTerm) return setOptions([]);
    const axiosInstance = createAxiosInstance();
    const response = await axiosInstance.get(
      `${tenantVal}${GET_DISEASE_CODES}/search/${searchTerm.replace(
        /\//g,
        "-"
      )}`
    );
    if (response?.data?.length > 0) {
      setOptions(
        response.data.map((p) => ({
          id: p.code,
          title: `${p.code} - ${p.name}`,
        }))
      );
    } else {
      setOptions([]);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "familyRelationship",
        header: "Relationship",
        width: "120px",
        render: (item) => item.familyRelationship || "-",
      },
      {
        id: "diseaseCode",
        header: "Disease Code",
        width: "130px",
        render: (item) => item.diseaseCode || "-",
      },
      {
        id: "medicalDetails",
        header: "Medical Details",
        width: "minmax(min-content, 1fr)",
        render: (item) => item.medicalDetails || "-",
        getTooltipText: (item) => item.medicalDetails || "-",
      },
    ],
    []
  );

  const rowActions = useMemo(
    () => [
      {
        label: "Edit",
        icon: Pencil,
        primary: true,
        onClick: (item) => handleOpenModal(item.id),
      },
      {
        label: "Delete",
        icon: Trash2,
        primary: true,
        onClick: (item) => setItemToDelete(item),
      },
    ],
    [handleOpenModal]
  );

  return (
    <>
      <div className={s.cardWrapper}>
        <div className={s.topWrapper}>
          <h3 className={s.title}>Family Medical History</h3>
          <Button variant="text" onClick={handleOpenModal} endIcon={Plus}>
            Add new
          </Button>
        </div>

        <DataTable
          columns={columns}
          data={familyData}
          getRowId={(row) => row.id}
          isLoading={isLoading}
          isError={isError}
          errorLabel="this patient's family medical history"
          onRetry={refetch}
          emptyIllustration={MedicalHistoryEmptyState}
          emptyTitle="No Family Medical History"
          emptyText="Once a doctor adds family medical history, it will appear here."
          rowActions={rowActions}
          rowsPerPage={20}
          rowsOptions={[5, 10, 20, 50, 100, "All"]}
        />
      </div>

      <Dialog
        title="Family Medical History"
        width={560}
        onClose={handleCloseModal}
        open={isModalOpen.open}
      >
        <form>
          <div className={s.inputWrapper}>
            <p className={s.rowTitle}>Family Relationship</p>
            <Select
              options={[
                "FATHER",
                "MOTHER",
                "DAUGHTER",
                "SON",
                "GRANDFATHER",
                "GRANDMOTHER",
                "CARER",
                "OTHER",
              ]}
              value={familyRelationship ?? "FATHER"}
              onChange={(value) => setFamilyRelationship(value)}
              required
            />
          </div>
          <div className={s.inputWrapper}>
            <p className={s.rowTitle}>Medical Details</p>
            <textarea
              placeholder="type here"
              className={s.textarea}
              value={medicalDetails ?? ""}
              type="text"
              rows="2"
              maxLength="200"
              onChange={(e) => setMedicalDetails(e.target.value)}
            />
          </div>
          <div className={s.inputWrapper}>
            <p className={s.rowTitle}>Disease Code</p>
            <Autocomplete
              disablePortal
              id="combo-box-demo"
              value={fullName || null}
              options={options}
              getOptionLabel={(option) => option?.title ?? fullName}
              onInputChange={onInputChange}
              onChange={(_event, newValue) => {
                setFullName(newValue ? newValue.title : "");
                setDiseaseCodeData(newValue?.id);
              }}
              renderInput={(params) => (
                <TextField {...params} label="" placeholder="Search here..." />
              )}
            />
          </div>
          <div className={s.inputWrapper}>
            <p className={s.rowTitle}>Deceased?</p>
            <Select
              options={["YES", "NO", "UNKNOWN"]}
              value={deceased ?? "NO"}
              onChange={(value) => setDeceased(value)}
              required
            />
          </div>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button onClick={handleSave2}>Save</Button>
        </DialogFooter>
      </Dialog>

      <ConfirmDialog
        isOpen={!!itemToDelete}
        onClose={() => setItemToDelete(null)}
        onConfirm={async () => {
          if (itemToDelete) {
            setIsDeleting(true);
            try {
              await deleteMedicalHistory(itemToDelete.id);
              setItemToDelete(null);
            } finally {
              setIsDeleting(false);
            }
          }
        }}
        isLoading={isDeleting}
        title="Delete family medical history"
        message="Are you sure you want to delete this family medical history entry? This action cannot be undone."
        confirmLabel="Delete"
      />
    </>
  );
};

export { FamilyMedicalHistory };
